/**
 * [ADR-008] — anónimo primeiro.
 *
 * A app entra sozinha na primeira visita e o progresso começa a acumular sem
 * ninguém pedir nada. Ligar conta é uma decisão posterior, e `linkWithCredential`
 * mantém o mesmo uid — o progresso não migra, porque nunca muda de dono.
 *
 * Tudo isto está atrás de uma interface por uma razão prática: sem projecto
 * Firebase configurado (que é o estado de qualquer máquina de desenvolvimento
 * hoje), a app continua a funcionar por inteiro com uma identidade local. O
 * que **não** acontece é a app fingir que está autenticada contra um servidor
 * a sério; o servidor tem o seu próprio interruptor, e recusa arrancar com os
 * dois ligados ao mesmo tempo.
 */

import type { Auth as FirebaseAuth } from 'firebase/auth';
/** O módulo inteiro, tipado sem o carregar: o `import()` é em tempo de execução. */
import type * as FirebaseAuthNs from 'firebase/auth';

type FirebaseAuthModule = typeof FirebaseAuthNs;

export type AuthProvider = 'google' | 'apple' | 'email';

export interface AuthUser {
  readonly uid: string;
  readonly isAnonymous: boolean;
  readonly displayName: string | null;
  readonly email: string | null;
}

export type LinkOutcome =
  | { readonly ok: true; readonly user: AuthUser }
  /** A credencial já pertence a outra conta: é aqui que o merge entra (§47). */
  | { readonly ok: false; readonly reason: 'credential_in_use'; readonly previousToken: string }
  | { readonly ok: false; readonly reason: 'cancelled' | 'unavailable'; readonly detail?: string };

export interface AuthClient {
  readonly kind: 'firebase' | 'local';
  current(): AuthUser | null;
  subscribe(fn: (user: AuthUser | null) => void): () => void;
  ensureSignedIn(): Promise<AuthUser>;
  link(provider: AuthProvider, email?: string): Promise<LinkOutcome>;
  token(): Promise<string | null>;
  signOut(): Promise<void>;
}

export interface FirebaseWebConfig {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly appId: string;
  /**
   * Só o Analytics precisa disto, e por isso é opcional: sem ele o `auth` e o
   * `app-check` funcionam na mesma. Exigi-lo faria a configuração inteira
   * devolver `null` num sítio onde não faltava nada de que ela depende.
   */
  readonly measurementId?: string;
}

const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};

export function firebaseConfigFromEnv(): FirebaseWebConfig | null {
  const apiKey = env['VITE_FIREBASE_API_KEY'];
  const authDomain = env['VITE_FIREBASE_AUTH_DOMAIN'];
  const projectId = env['VITE_FIREBASE_PROJECT_ID'];
  const appId = env['VITE_FIREBASE_APP_ID'];
  if (!apiKey || !authDomain || !projectId || !appId) return null;
  const measurementId = env['VITE_FIREBASE_MEASUREMENT_ID'];
  return { apiKey, authDomain, projectId, appId, ...(measurementId ? { measurementId } : {}) };
}

// ── identidade local ───────────────────────────────────────────

const LOCAL_KEY = 'et-local-uid';

/**
 * Identidade de desenvolvimento.
 *
 * O uid vive no `localStorage` e o token **é** o uid. Isto só funciona contra
 * uma API arrancada com `DEV_AUTH=true`, que por sua vez se recusa a arrancar
 * com um projecto Firebase configurado. São duas fechaduras para a mesma
 * porta, porque uma porta destas aberta em produção é o pior tipo de bug.
 */
export function localAuthClient(): AuthClient {
  const listeners = new Set<(u: AuthUser | null) => void>();
  const read = (): AuthUser | null => {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      return raw === null ? null : JSON.parse(raw) as AuthUser;
    } catch { return null; }
  };
  const write = (u: AuthUser | null) => {
    try {
      if (u === null) localStorage.removeItem(LOCAL_KEY);
      else localStorage.setItem(LOCAL_KEY, JSON.stringify(u));
    } catch { /* modo privado: a sessão vive só nesta página */ }
    for (const fn of listeners) fn(u);
  };

  let memory: AuthUser | null = read();

  return {
    kind: 'local',
    current: () => memory,
    subscribe(fn) {
      listeners.add(fn);
      fn(memory);
      return () => listeners.delete(fn);
    },
    async ensureSignedIn() {
      if (memory !== null) return memory;
      const uid = `anon-${Math.random().toString(36).slice(2, 10)}`;
      memory = { uid, isAnonymous: true, displayName: null, email: null };
      write(memory);
      return memory;
    },
    async link(provider, email) {
      if (memory === null) await this.ensureSignedIn();
      // Sem provedor real, ligar conta é uma promoção da mesma identidade:
      // deixa de ser anónima e passa a ter nome. É o suficiente para exercitar
      // o aviso de progresso por proteger e o resto do fluxo.
      memory = {
        uid: memory!.uid,
        isAnonymous: false,
        displayName: provider === 'email' ? (email ?? null) : `local:${provider}`,
        email: email ?? null,
      };
      write(memory);
      return { ok: true, user: memory };
    },
    async token() {
      if (memory === null) return null;
      // O uid nao muda ao ligar conta (o progresso nao muda de dono), por isso
      // o estado de ligacao viaja a parte. Ver `devVerifier` do lado do servidor.
      return memory.isAnonymous ? memory.uid : `${memory.uid}#linked`;
    },
    async signOut() { memory = null; write(null); },
  };
}

// ── Firebase ───────────────────────────────────────────────────

const toUser = (u: {
  uid: string; isAnonymous: boolean; displayName: string | null; email: string | null;
}): AuthUser => ({
  uid: u.uid, isAnonymous: u.isAnonymous, displayName: u.displayName, email: u.email,
});

/**
 * O SDK entra por `import()` para não pesar no arranque de quem só quer ler.
 * A app é utilizável inteira sem conta; carregar o Firebase antes de alguém
 * querer guardar alguma coisa seria pagar por uma funcionalidade opcional.
 */
export function firebaseAuthClient(config: FirebaseWebConfig): AuthClient {
  let mod: FirebaseAuthModule | null = null;
  let auth: FirebaseAuth | null = null;
  const listeners = new Set<(u: AuthUser | null) => void>();
  let currentUser: AuthUser | null = null;

  const load = async () => {
    if (auth !== null && mod !== null) return { mod, auth };
    const [app, authMod] = await Promise.all([
      import('firebase/app'),
      import('firebase/auth'),
    ]);
    const instance = app.getApps().length > 0 ? app.getApp() : app.initializeApp(config);
    mod = authMod;
    auth = authMod.getAuth(instance);
    authMod.onAuthStateChanged(auth, (u) => {
      currentUser = u === null ? null : toUser(u);
      for (const fn of listeners) fn(currentUser);
    });
    return { mod, auth };
  };

  /**
   * O provedor OAuth, construído e nunca guardado.
   *
   * `GoogleAuthProvider` é um descritor e não uma credencial: a credencial só
   * existe depois de a janela do Google devolver um token, e é por isso que a
   * ligação tem de passar por `linkWithPopup` e não por `linkWithCredential`.
   */
  const oauthProvider = (m: FirebaseAuthModule, provider: 'google' | 'apple') =>
    (provider === 'google' ? new m.GoogleAuthProvider() : new m.OAuthProvider('apple.com'));

  const sendEmailLink = async (email?: string) => {
    const { mod: m, auth: a } = await load();
    if (email === undefined) throw new Error('email em falta');
    // Sem passwords, por decisão: menos superfície e nada de recuperação de conta.
    await m.sendSignInLinkToEmail(a, email, {
      url: window.location.href, handleCodeInApp: true,
    });
  };

  return {
    kind: 'firebase',
    current: () => currentUser,
    subscribe(fn) {
      listeners.add(fn);
      fn(currentUser);
      void load();
      return () => listeners.delete(fn);
    },
    async ensureSignedIn() {
      const { mod: m, auth: a } = await load();
      if (a.currentUser !== null) return toUser(a.currentUser);
      const cred = await m.signInAnonymously(a);
      currentUser = toUser(cred.user);
      return currentUser;
    },
    async link(provider, email) {
      const { mod: m, auth: a } = await load();
      if (a.currentUser === null) await this.ensureSignedIn();
      try {
        if (provider === 'email') {
          // O link vai por email e a ligação conclui-se no regresso, noutra
          // visita. Daqui não sai um utilizador ligado, e dizer que sim seria
          // a app afirmar uma coisa que ainda não aconteceu.
          await sendEmailLink(email);
          return { ok: false, reason: 'cancelled', detail: 'email_link_sent' };
        }
        const result = await m.linkWithPopup(a.currentUser!, oauthProvider(m, provider));
        currentUser = toUser(result.user);
        return { ok: true, user: currentUser };
      } catch (err) {
        const code = (err as { code?: string }).code ?? '';
        if (code === 'auth/credential-already-in-use' || code === 'auth/email-already-in-use') {
          // O uid anónimo vai perder-se ao entrar na conta que já existia.
          // Guarda-se o token dele para a API poder migrar o progresso.
          const previousToken = await a.currentUser!.getIdToken();
          return { ok: false, reason: 'credential_in_use', previousToken };
        }
        /*
          Um popup bloqueado nao e um cancelamento.

          Quando o browser recusa abrir a janela, o SDK rejeita com um codigo
          que diz exactamente isso. Tratar tudo como "cancelled" calava a app
          num caso em que a pessoa nao cancelou nada: carregou no botao e nao
          viu acontecer nada. Sai `unavailable`, para a interface poder dizer
          o que se passou.
        */
        if (code === 'auth/popup-blocked'
          || code === 'auth/operation-not-supported-in-this-environment'
          || code === 'auth/unauthorized-domain') {
          return { ok: false, reason: 'unavailable', detail: code };
        }
        return { ok: false, reason: 'cancelled', detail: code };
      }
    },
    async token() {
      const { auth: a } = await load();
      return a.currentUser === null ? null : a.currentUser.getIdToken();
    },
    async signOut() {
      const { mod: m, auth: a } = await load();
      await m.signOut(a);
      currentUser = null;
    },
  };
}

let singleton: AuthClient | null = null;

export function authClient(): AuthClient {
  if (singleton !== null) return singleton;
  const config = firebaseConfigFromEnv();
  singleton = config === null ? localAuthClient() : firebaseAuthClient(config);
  return singleton;
}

/** Para testes: injectar um cliente e voltar atrás. */
export function setAuthClient(client: AuthClient | null): void {
  singleton = client;
}
